/**
 * Game Restriction Composable
 * 遊戲限制檢查
 *
 * 架構：
 * - 單例模式：使用 nuxtApp._gameRestrictionContext 避免 storeToRefs 重複創建
 */

import { storeToRefs } from 'pinia'

import { useMainStore } from '@/stores/main'

export const useGameRestriction = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 storeToRefs 只創建一次
  if (!nuxtApp._gameRestrictionContext) {
    const mainStore = useMainStore()
    const { getWithdrawRule } = storeToRefs(mainStore)
    const { setModal, setModalParams } = mainStore

    /**
     * Check if the game matches the limit rules (Only these games are allowed)
     * @param {Object} game
     * @returns {Boolean} true if the game is allowed
     */
    const isGameLimited = (game) => {
      const { providerId, gameId, mappingId, gameTypeId } = game
      return (getWithdrawRule.value?.limitGameDetail || []).some((item) => {
        return (
          (!item.provider || item.provider === providerId) &&
          (!item.gameTypeId || item.gameTypeId === gameTypeId) &&
          (!item.gameTypeMappingId || item.gameTypeMappingId === mappingId) &&
          (!item.gameId || item.gameId === gameId)
        )
      })
    }

    /**
     * Check if the game matches the block rules (These games are blocked)
     * @param {Object} game
     * @returns {Boolean} true if the game is blocked
     */
    const isGameBlocked = (game) => {
      const { providerId, gameId, mappingId } = game
      return (getWithdrawRule.value?.blockLimitGameDetail ?? []).some((item) => {
        // 如果有指定 gameId，只比對 gameId
        if (item.gameId) {
          return item.gameId === gameId
        }

        // 必須至少設定 provider 或 gameTypeMappingId 其中之一，否則不應視為符合
        if (!item.provider && !item.gameTypeMappingId) return false

        const isProviderMatch = !item.provider || item.provider === providerId
        const isMappingMatch = !item.gameTypeMappingId || item.gameTypeMappingId === mappingId

        return isProviderMatch && isMappingMatch
      })
    }

    /**
     * Check if the game is restricted by either limit or block rules.
     * Open ModalNotBonusGame if restricted.
     * @param {Object} game
     * @returns {Boolean} true if restricted (should stop execution), false if allowed
     */
    const checkGameRestriction = (game, entrySource) => {
      const limitGameDetail = getWithdrawRule.value?.limitGameDetail || []

      // 檢查 limitGameDetail（限制遊戲）
      if (limitGameDetail.length > 0 && !isGameLimited(game)) {
        setModalParams({ game, entrySource })
        setModal('ModalNotBonusGame')
        return true
      }

      // 檢查 blockLimitGameDetail（封鎖遊戲）
      if (getWithdrawRule.value?.blockLimitGameDetail?.length > 0 && isGameBlocked(game)) {
        setModalParams({ game, entrySource })
        setModal('ModalNotBonusGame')
        return true
      }

      return false
    }

    // cleanup 方法：供 SSR cleanup plugin 調用（此 composable 無 watch 需清理）
    const cleanup = () => {}

    nuxtApp._gameRestrictionContext = {
      isGameLimited,
      isGameBlocked,
      checkGameRestriction,
      cleanup,
    }
  }

  return nuxtApp._gameRestrictionContext
}
